import { apiFetch, readJson, readErrorMessage } from './client';
import { listClientPortalTenants, type ClientPortalTenantOption } from './clientPortalStorageMountsAdmin';

export type { ClientPortalTenantOption };

export interface EntraGroupRow {
  id: string;
  displayName: string;
  mail: string | null;
  description: string | null;
  securityEnabled: boolean;
  groupTypes: string[];
  mailNickname: string | null;
  assignedSprRoles: string[];
  createdDateTime?: string | null;
}

export interface PortalAppRoleOption {
  id: string;
  value: string;
  displayName: string;
}

export interface ClientAccountsGroupsListResponse {
  items: EntraGroupRow[];
  portalAppRolesEnabled: boolean;
  portalAppRolesError: string | null;
}

function tenantBase(tenantId: string): string {
  return `/api/admin/client-accounts/tenants/${encodeURIComponent(tenantId)}`;
}

export { listClientPortalTenants };

export async function listClientAccountsGroups(tenantId: string): Promise<ClientAccountsGroupsListResponse> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientAccountsGroupsListResponse>(res);
}

export async function createClientAccountsGroup(
  tenantId: string,
  req: {
    displayName: string;
    description?: string | null;
    mailNickname?: string | null;
  }
): Promise<EntraGroupRow> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups`, {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EntraGroupRow>(res);
}

export async function updateClientAccountsGroup(
  tenantId: string,
  groupId: string,
  req: {
    displayName?: string | null;
    description?: string | null;
    mailNickname?: string | null;
    assignedSprRoles?: string[] | null;
  }
): Promise<EntraGroupRow> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups/${encodeURIComponent(groupId)}`, {
    method: 'PATCH',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EntraGroupRow>(res);
}

export async function deleteClientAccountsGroup(tenantId: string, groupId: string): Promise<void> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function listPortalAppRoles(tenantId: string): Promise<PortalAppRoleOption[]> {
  const res = await apiFetch(`${tenantBase(tenantId)}/portal-app-roles`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<PortalAppRoleOption[]>(res);
}

export async function getGroupAppRoles(tenantId: string, groupId: string): Promise<string[]> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups/${encodeURIComponent(groupId)}/app-roles`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<string[]>(res);
}

export async function setGroupAppRoles(
  tenantId: string,
  groupId: string,
  roleValues: string[],
  options?: { newGroup?: boolean }
): Promise<string[]> {
  const qs = options?.newGroup ? '?newGroup=true' : '';
  const res = await apiFetch(`${tenantBase(tenantId)}/groups/${encodeURIComponent(groupId)}/app-roles${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ roleValues })
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<string[]>(res);
}
